import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'

export function useTaskMentionAutocomplete(textareaRef, projectIdRef) {
  const showAutocomplete = ref(false)
  const autocompletePosition = ref({ x: 0, y: 0 })
  const selectedIndex = ref(0)
  const mentionQuery = ref('')
  const mentionStartPos = ref(0)
  const activeTextarea = ref(null)

  // GraphQL query to search tasks
  const TASKS_SEARCH_QUERY = gql`
    query SearchTasks($projectId: ID!, $search: String!, $first: Int!) {
      project(id: $projectId) {
        id
        tasks(search: $search, first: $first) {
          edges {
            node {
              id
              iid
              title
              archived
              workflowStatus {
                id
                name
                color
              }
            }
          }
        }
      }
    }
  `

  // Fetch tasks when query changes
  const searchQuery = computed(() => {
    const query = mentionQuery.value.trim()
    // Only search if query is at least 1 character (for ID search) or 3 characters (for title search)
    if (!query || query.length < 1) return null
    return query
  })

  const { result: tasksResult, loading: tasksLoading } = useQuery(
    TASKS_SEARCH_QUERY,
    () => ({
      projectId: projectIdRef?.value || null,
      search: searchQuery.value || '',
      first: 20,
    }),
    () => ({
      enabled: !!projectIdRef?.value && !!searchQuery.value && showAutocomplete.value,
    })
  )

  // Extract tasks from GraphQL result
  const filteredTasks = computed(() => {
    if (!tasksResult.value?.project?.tasks) return []

    const tasks = tasksResult.value.project.tasks.edges.map(edge => edge.node)

    // Filter out archived tasks
    const activeTasks = tasks.filter(task => !task.archived)

    // If query looks like a number (task IID), prioritize exact IID match
    const query = mentionQuery.value.trim()
    if (/^\d+$/.test(query)) {
      const taskIid = parseInt(query, 10)
      const exactMatch = activeTasks.find(task => {
        return Number(task.iid) === taskIid
      })

      if (exactMatch) {
        return [exactMatch, ...activeTasks.filter(t => t.id !== exactMatch.id)]
      }
    }

    return activeTasks
  })

  // Handle click outside to close autocomplete
  const handleClickOutside = event => {
    if (showAutocomplete.value) {
      const textarea = textareaRef.value
      const clickedOnTextarea =
        textarea && (event.target === textarea || textarea.contains(event.target))
      const clickedOnAutocomplete = event.target.closest('.task-autocomplete')

      if (!clickedOnTextarea && !clickedOnAutocomplete) {
        hideAutocomplete()
      }
    }
  }

  // Close autocomplete on scroll (prevents dropdown from being disconnected from textarea)
  const handleScroll = () => {
    if (showAutocomplete.value) {
      hideAutocomplete()
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside, true)
    window.addEventListener('scroll', handleScroll, true)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside, true)
    window.removeEventListener('scroll', handleScroll, true)
  })

  // Get the active textarea
  const getActiveTextarea = () => {
    return activeTextarea.value || textareaRef.value
  }

  // Check if # was typed and update autocomplete state
  const checkForTaskMention = () => {
    const textarea = getActiveTextarea()
    if (!textarea) {
      hideAutocomplete()
      return false
    }

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = textarea.value.substring(0, cursorPos)

    // Find the last # symbol before cursor
    const lastHashPos = textBeforeCursor.lastIndexOf('#')

    // Check if # is followed by space, newline, or end of string
    if (lastHashPos === -1) {
      hideAutocomplete()
      return false
    }

    // Check if there's a space or newline between # and cursor
    const textAfterHash = textBeforeCursor.substring(lastHashPos + 1)
    if (textAfterHash.includes(' ') || textAfterHash.includes('\n')) {
      hideAutocomplete()
      return false
    }

    // Extract query (text after #)
    const query = textAfterHash.trim()
    mentionQuery.value = query
    mentionStartPos.value = lastHashPos + 1

    // Show autocomplete if we have tasks to show (will be populated by GraphQL query)
    if (projectIdRef?.value) {
      updateAutocompletePosition(textarea, cursorPos)
      showAutocomplete.value = true
      selectedIndex.value = 0
      return true
    } else {
      hideAutocomplete()
      return false
    }
  }

  // Update autocomplete dropdown position
  const updateAutocompletePosition = (textarea, cursorPos) => {
    const rect = textarea.getBoundingClientRect()
    const dropdownMaxHeight = 250 // Approximate max height of dropdown
    const gap = 4

    // Calculate available space above and below
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap

    // Position dropdown aligned to the left of textarea
    const x = Math.max(8, Math.min(rect.left, window.innerWidth - 270))

    // Decide whether to position above or below
    const positionAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow

    let y
    if (positionAbove) {
      // Position above: anchor bottom edge just above the textarea
      y = rect.top - gap
    } else {
      // Position below: anchor top edge just below the textarea
      y = rect.bottom + gap
    }

    autocompletePosition.value = { x, y, positionAbove }
  }

  // Insert selected task mention
  const insertTaskMention = task => {
    const textarea = getActiveTextarea()
    if (!textarea || !task || !task.iid) return

    const taskIid = String(task.iid)

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const textBefore = textarea.value.substring(0, mentionStartPos.value - 1) // Before #
    const textAfter = textarea.value.substring(end)

    const newValue = textBefore + `#${taskIid} ` + textAfter
    const newCursorPos = textBefore.length + taskIid.length + 2 // After #taskIid and space

    // Update textarea value
    textarea.value = newValue

    // Trigger input event for v-model
    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
    })
    textarea.dispatchEvent(inputEvent)

    const changeEvent = new Event('change', { bubbles: true })
    textarea.dispatchEvent(changeEvent)

    // Set cursor position
    nextTick(() => {
      textarea.setSelectionRange(newCursorPos, newCursorPos)
      textarea.focus()
    })

    hideAutocomplete()
  }

  // Handle keyboard navigation
  const handleKeyDown = event => {
    // Store the active textarea from the event target
    if (event.target && event.target.tagName === 'TEXTAREA') {
      activeTextarea.value = event.target
    }

    // If # was typed, check for mention after it's inserted
    if (!showAutocomplete.value && event.key === '#') {
      // Let # be inserted first, then check
      setTimeout(() => checkForTaskMention(), 10)
      return false
    }

    if (!showAutocomplete.value) {
      return false
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        event.stopPropagation()
        selectedIndex.value = Math.min(selectedIndex.value + 1, filteredTasks.value.length - 1)
        return true

      case 'ArrowUp':
        event.preventDefault()
        event.stopPropagation()
        selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
        return true

      case 'Enter':
      case 'Tab':
        event.preventDefault()
        event.stopPropagation()
        if (filteredTasks.value[selectedIndex.value]) {
          insertTaskMention(filteredTasks.value[selectedIndex.value])
        }
        return true

      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        hideAutocomplete()
        return true

      case 'Backspace':
        // Re-check mention after backspace
        setTimeout(() => checkForTaskMention(), 0)
        break

      default:
        // For typing, re-check mention
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          setTimeout(() => checkForTaskMention(), 0)
        }
        break
    }

    return false
  }

  // Handle input event (for v-model changes)
  const handleInput = event => {
    // Store the active textarea from the event target
    if (event && event.target && event.target.tagName === 'TEXTAREA') {
      activeTextarea.value = event.target
    }
    // Always check for task mentions on input
    checkForTaskMention()
  }

  const hideAutocomplete = () => {
    showAutocomplete.value = false
    selectedIndex.value = 0
    mentionQuery.value = ''
  }

  return {
    showAutocomplete,
    autocompletePosition,
    filteredTasks,
    selectedIndex,
    mentionQuery,
    tasksLoading,
    checkForTaskMention,
    handleKeyDown,
    handleInput,
    insertTaskMention,
    hideAutocomplete,
  }
}
